<template>
    <rkm-modal
        ref="RkmModal"
        large
        @save="handleSave"
        title="Pedido de Exames"
        :buttons="['cancel', 'save']"
    >
        <rkm-form ref="RkmForm">
            <rkm-row>
                <rkm-col cols="7">
                    <rkm-form-date-picker
                        label="Data Solicitação"
                        :max-date="dataAtual"
                        v-model="model.dataSolicitacao"
                        :validations="validations.dataSolicitacao"
                    />
                </rkm-col>

                <rkm-col cols="auto">
                    <rkm-form-checkbox
                        v-model="model.urgente"
                        :label="false"
                    >
                        URGENTE
                    </rkm-form-checkbox>
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col cols="7">
                    <rkm-form-input-autocomplete
                        label="Unidade solicitante"
                        :searchFields="fields.autocomplete.unidadeSolicitante"
                        :repository="repositoryAutoCompleteUnidadeEncaminhamento"
                        textField="nomeFantasia"
                        v-model="form.unidadeEncaminhamento"
                        :validations="validations.unidadeEncaminhamento"
                        uppercase
                    >
                        <rkm-input-group-prepend slot="prepend">
                            <rkm-form-select
                                :options="data.form.origensUnidade"
                                @change="handleChangeOrigemUnidadeEncaminhamento()"
                                class="m-0"
                                textField="nome"
                                v-model="form.origemUnidadeValue"
                                valueField="id"
                            />
                        </rkm-input-group-prepend>

                        <rkm-input-group-append
                            v-if="isOrigemExterna"
                            slot="append"
                        >
                            <rkm-button
                                color="primary"
                                title="Cadastrar unidade"
                                @click="$refs.UnidadeExterna.open()"
                            >
                                <rkm-icon icon="plus" />
                            </rkm-button>
                        </rkm-input-group-append>
                    </rkm-form-input-autocomplete>
                </rkm-col>

                <rkm-col cols="5">
                    <rkm-form-input
                        :validations="validations.medicoSolicitanteEncaminhamento"
                        label="Médico solicitante"
                        uppercase
                        v-model="model.medicoSolicitanteEncaminhamento"
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row v-if="data.form.recepcao.isEncaminhamento && tipoExameId === this.enums.tipoExame.IMAGEM">
                <rkm-col cols="12">
                    <rkm-form-select
                        :disabled="model.exames.length > 0"
                        :options="data.form.medicosComPlantao"
                        :validations="validations.lotacaoExecutante"
                        label="Profissional executante"
                        show-default-option
                        text-field="descricao"
                        v-model="model.lotacaoExecutanteId"
                        value-field="id"
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col class="flex-grow-1">
                    <rkm-form-input-autocomplete
                        :disabled="data.form.medicosComPlantao && !model.lotacaoExecutanteId"
                        :repository="repositories.exame"
                        :requestQueryStringParams="autocompletesRequestParams.queryString.exame"
                        :searchFields="form.autocompleteFields.exame"
                        label="Exame"
                        v-model="form.exame"
                    >
                        <template v-slot:itemLabel="{ item }">
                            {{ item.codigo }} - {{ item.nome }}
                        </template>
                    </rkm-form-input-autocomplete>
                </rkm-col>

                <rkm-col class="flex-grow-0">
                    <rkm-button
                        color="primary"
                        @click.stop="addExame()"
                        :label="false"
                    >
                        Incluir
                    </rkm-button>
                </rkm-col>
            </rkm-row>

            <rkm-table
                ref="datatable"
                :fields="fields.exame"
                :items="model.exames"
                tag="div"
                hide-pagination
                hide-filters
            >
                <template v-slot:cell(actions)="{ index }">
                    <rkm-button
                        color="danger"
                        @click="removeExame(index)"
                        small
                    >
                        <rkm-icon icon="trash" />
                    </rkm-button>
                </template>
            </rkm-table>

            <rkm-row>
                <rkm-col>
                    <rkm-form-input
                        label="Justifivativa"
                        v-model="model.justificativa"
                    />
                </rkm-col>
            </rkm-row>
        </rkm-form>

        <unidade-externa
            ref="UnidadeExterna"
            @save="form.unidadeEncaminhamento = $event"
        />
    </rkm-modal>
</template>

<script>
// Mixins.
import { repository, error } from '@/mixins'

// Moment.
import moment from 'moment'

// Enums.
import statusExame from '@/enums/pronto-atendimento/statusExame'
import origemUnidadeEncaminhamento from '@/enums/pronto-atendimento/origemUnidadeEncaminhamento'
import tipoExame from '@/enums/pronto-atendimento/parametrizacao/tipoExame'

// mapGetters.
import { mapGetters } from "vuex";

// Components.
import UnidadeExterna from '@/components/cadastro/unidade-externa/Modal'

export default {
    components: {
        UnidadeExterna,
    },
    created() {

        this.repositories = {
            exame: this.getConectaSusRepository('parametrizacao.exame'),
            unidade: this.getConectaSusRepository('cadastro.estabelecimento.unidade'),
            unidadeExterna: this.getConectaSusRepository('cadastro.estabelecimento.unidadeExterna'),
        }
    },
    computed: {
        validations() {

            return {
                medico: { required: true, max: 150, min: 3 },
                unidade: { required: true, max: 150, min: 3 },
                dataSolicitacao: { required: true },
                unidadeEncaminhamento: { required: true },
                medicoSolicitanteEncaminhamento: {
                    required: true,
                    min: 6,
                    max: 150,
                    name: true,
                },
                lotacaoExecutante: { required: true },
            }
        },
        autocompletesRequestParams() {
            let cbo = this.cboLotacaoAtual

            if (this.data.form.recepcao.isEncaminhamento && this.model.lotacaoExecutanteId) {
                cbo = this.data.form.medicosComPlantao.find(m => m.id == this.model.lotacaoExecutanteId).cbo
            }

            return {
                queryString: {
                    exame: {
                        exames: true,
                        cbo_lotacao_atual: cbo,
                        paciente_id: this.data ? this.data.form.paciente.id : null,
                        tipo_id: this.tipoExameId,
                    },
                }
            }
        },
        fields() {
            return {
                autocomplete: {
                    unidadeSolicitante: [
                        { key: 'nome_fantasia', searchable: true },
                    ]
                },
                exame: [
                    { key: 'exame.nome', label: 'Nome' },
                    { key: 'exame.codigo', label: 'Codigo' },
                    { key: 'actions', label: 'Ações', class: 'col-min' }
                ],
            }
        },
        repositoryAutoCompleteUnidadeEncaminhamento() {

            return this.form.origemUnidadeValue === this.enums.origemUnidadeEncaminhamento.INTERNA
                ? this.repositories.unidade
                : this.repositories.unidadeExterna
        },
        isOrigemExterna() {

            return this.form.origemUnidadeValue === this.enums.origemUnidadeEncaminhamento.EXTERNA
        },
    },
    data: () => ({
        model: {
            unidadeSolicitante: null,
            medicoSolicitante: null,
            dataSolicitacao: null,
            justificativa: null,
            urgente: false,
            exames: [],
            files: [],
            unidadeExternaEncaminhamentoId: null,
            unidadeEncaminhamentoId: null,
            medicoSolicitanteEncaminhamento: null,
            lotacaoExecutanteId: null,
        },
        form: {
            autocompleteFields: {
                exame: [
                    { key: 'codigo', searchable: true },
                    { key: 'nome', searchable: true }
                ]
            },
            exame: null,
            origemUnidadeValue: null,
            unidadeEncaminhamento: null,
        },
        enums: { statusExame, origemUnidadeEncaminhamento, tipoExame },
        dataAtual: null,
    }),
    methods: {
        ...mapGetters(['getLotacao', 'getCentroCusto']),
        getUnidadeEncaminhamento() {

            if (this.data.form.recepcao.unidadeEncaminhamento) {

                return this.data.form.recepcao.unidadeEncaminhamento
            }

            if (this.data.form.recepcao.unidadeExternaEncaminhamento) {

                return this.data.form.recepcao.unidadeExternaEncaminhamento
            }

            return null
        },
        open(dataAtual) {

            this.dataAtual = dataAtual
            this.form.origemUnidadeValue = this.data.form.recepcao.origemUnidadeEncaminhamento
                || this.enums.origemUnidadeEncaminhamento.INTERNA

            if (this.data.form.recepcao.origemUnidadeEncaminhamento != null) {

                this.form.unidadeEncaminhamento = this.getUnidadeEncaminhamento()
                this.model.medicoSolicitanteEncaminhamento = this.data.form.recepcao.medicoSolicitanteEncaminhamento
            }

            this.$refs.RkmModal.show()
        },
        handleSave() {

            this.$refs.RkmForm.validate()
                .then(valid => {

                    if (!valid) return

                    if (this.model.exames.length === 0) {

                        this.createNotification({
                            type: 'alert',
                            color: 'danger',
                            title: 'Ação não permitida',
                            message: 'Inclua pelo menos um exame.'
                        })

                        return
                    }

                    this.model.dataSolicitacao =
                        moment(this.model.dataSolicitacao).format('DD/MM/YYYY')

                    this.model.unidadeEncaminhamentoId = this.getUnidadeEncaminhamentoId(
                        this.enums.origemUnidadeEncaminhamento.INTERNA
                    )

                    this.model.unidadeExternaEncaminhamentoId = this.getUnidadeEncaminhamentoId(
                        this.enums.origemUnidadeEncaminhamento.EXTERNA
                    ),

                    this.$emit('createPedidoExame', this.model)
                    this.$refs.RkmModal.hide()
                })
        },
        getUnidadeEncaminhamentoId(origemUnidade) {

            if (this.form.origemUnidadeValue === origemUnidade) {

                return this.form.unidadeEncaminhamento ? this.form.unidadeEncaminhamento.id : null
            }

            return null
        },
        handleChangeOrigemUnidadeEncaminhamento() {

            this.form.unidadeEncaminhamento = null
        },
        findExame(exameId) {
            return this.model.exames.find(item => item.exame.id === exameId) ? true : false
        },
        addExame() {

            let exame = this.form.exame

            if(!exame) {

                this.createNotification({
                    type: 'alert',
                    title: 'Aviso',
                    message: 'É necessário preencher o campo exame para adiciona-lo.',
                    color: 'primary'
                })

            } else if(this.findExame(exame.id)) {

                this.createNotification({
                    type: 'alert',
                    title: 'Aviso',
                    message: 'Este exame já foi incluído para este pedido!',
                    color: 'primary'
                })

            } else if (exame.quantidadeMaximaExecucao < 1) {

                this.createNotification({
                    type: 'alert',
                    title: 'Ação não permitida',
                    message: 'Quantidade máxima atingida para este procedimento.',
                    color: 'danger'
                })

            } else {

                exame.lotacao = this.getLotacao()
                exame.centroCusto = this.getCentroCusto()

                let lotacao = {
                    ocupacao: {
                        codigo: exame.lotacao.ocupacao.codigo,
                        nome: exame.lotacao.ocupacao.nome
                    },
                    profissional: {
                        nome: exame.lotacao.usuario.cidadao.nome
                    }
                }

                exame.procedimento = {
                    nome: exame.procedimentoNome,
                    codigo: exame.codigo
                }

                let status = {
                    id: 1,
                    nome: 'SOLICITADO'
                }

                this.model.exames.push({
                    observacao: null,
                    disabled: exame.tipoId !== this.tipoExameId,
                    exame,
                    status,
                    lotacao,
                    horarioRealizacao: null,
                    lotacaoExecutanteId: null,
                })

                this.form.exame = null
            }
        },
        removeExame(index) {
           this.model.exames.splice(index, 1)
        }
    },
    mixins: [ repository, error ],
    props: {
        data: { type: Object, required: true },
        tipoExameId: { type: Number, required: true },
        cboLotacaoAtual: { type: Boolean, default: true }
    },
}
</script>